// Nothing in the app shows until someone on the team is signed in and past 2-step.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { getAuthFlow, FAKE_AUTH } from "./auth-context";
import { useAuthState, type AuthFlow, type AuthState } from "./auth-flow";
import { SignInScreen } from "./SignInScreen";
import type { Member } from "./auth-api";
import { practiceAs } from "@/practice/mode";
import { BootShell } from "@/ui/BootShell";

/** `warm` starts fetching what comes after sign-in, once the person starts on the sign-in screen. */
export function AuthGate({ warm, children }: { warm?: () => unknown; children: ReactNode }) {
  const [flow, setFlow] = useState<AuthFlow | null>(null);
  useEffect(() => {
    void getAuthFlow().then(setFlow);
  }, []);
  if (!flow) return <BootShell />;
  return (
    <Gate flow={flow} warm={warm}>
      {children}
    </Gate>
  );
}

function Gate({ flow, warm, children }: { flow: AuthFlow; warm: (() => unknown) | undefined; children: ReactNode }) {
  const state = useAuthState(flow);
  const member = state.step === "ready" ? state.member : null;
  const signingIn = state.step !== "ready" && state.step !== "loading";
  // the first tap or key on the sign-in screen, not its first paint: the screen itself never waits for the pages
  useEffect(() => {
    if (!signingIn || !warm) return;
    const events = ["pointerdown", "keydown", "input"] as const;
    const go = () => {
      stop();
      warm();
    };
    const stop = () => events.forEach((e) => document.removeEventListener(e, go, true));
    events.forEach((e) => document.addEventListener(e, go, true));
    return stop;
  }, [signingIn, warm]);
  // the moment someone gets in, fill the phone's copy so the field screens work with no signal
  useEffect(() => {
    if (!member) return;
    void (async () => {
      const data = await import("@/data/app-data");
      if (data.PRACTICE) await startPracticeFor(member);
      await data.refreshIfSignedIn();
      void data.getOutbox().kick();
    })();
  }, [member]);
  if (state.step === "ready") return <SignedIn.Provider value={state}>{children}</SignedIn.Provider>;
  return <SignInScreen flow={flow} state={state} />;
}

type Ready = Extract<AuthState, { step: "ready" }>;
const SignedIn = createContext<Ready | null>(null);

/** For component tests: a screen as someone signed in, without the sign-in flow. */
export function SignedInForTests({ role = "tech", aal = "aal1", children }: { role?: Member["role"]; aal?: "aal1" | "aal2"; children: ReactNode }) {
  const value = { step: "ready", member: { user_id: "u-test", email: "test@example.com", name: "Tess", role }, session: { userId: "u-test", email: "test@example.com", pwSet: true, aal } } as unknown as Ready;
  return <SignedIn.Provider value={value}>{children}</SignedIn.Provider>;
}

/**
 * Who is signed in, for screens inside the gate. `admin` is an owner who used 2-step this session (SPEC §3.2):
 * an owner without it works as a tech.
 */
export function useSignedIn(): { id: string; name: string; email: string; role: Member["role"]; admin: boolean } {
  const s = useContext(SignedIn);
  if (!s) throw new Error("useSignedIn() is only for screens inside the sign-in gate.");
  return { id: s.member.user_id, name: s.member.name, email: s.member.email ?? s.session.email, role: s.member.role, admin: s.member.role === "owner" && s.session.aal === "aal2" };
}

/** Practice: the sample warehouse, with the made-up team in the browser-test build, or just you otherwise. */
async function startPracticeFor(m: Member) {
  const { startPractice } = await import("@/practice/backend");
  const team = FAKE_AUTH || practiceAs() ? (await import("./fake-auth")).FAKE_PEOPLE.flatMap((p) => (p.member ? [{ ...p.member, email: p.member.email ?? "" }] : [])) : [{ ...m, email: m.email ?? "" }];
  startPractice({ id: m.user_id, email: m.email ?? "", name: m.name, role: m.role === "owner" ? "owner" : m.role === "tech" ? "tech" : "viewer" }, team);
}

/** The signed-in person, for screens that need the name or role. Only valid inside the gate. */
export function useMe(flow: AuthFlow) {
  const s = useAuthState(flow);
  return s.step === "ready" ? s : null;
}
